import React, { Fragment, useCallback, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { useDispatch } from "react-redux";
import {
  any,
  append,
  clone,
  concat,
  either,
  equals,
  filter,
  find,
  head,
  includes,
  isEmpty,
  join,
  length,
  map,
  mergeRight,
  omit,
  pipe,
  propEq,
  reject,
  uniqBy,
  values,
} from "ramda";
import { logOut } from "../../../auth/authActions";
import {
  normalizeAppliedFacets,
  normalizeFacetResults,
} from "../../../browse-media/redux/browseMediaHelpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSquareX } from "@fortawesome/pro-regular-svg-icons";
import { useMediaTable } from "../../common/use-mediaTable";

import ContentTable from "./ContentTable";
import ContentTotalsHeader from "./ContentTotalsHeader";
import ContentTypeFilters from "./ContentTypeFilters";
import ContentSearchBar from "./ContentSearchBar";
import EditSearchModal from "../../common/EditSearchModal";
import { anyTrue, isBlank, notBlank } from "../../../util/ext";
import { getTotalSelectedTerms } from "../../contentUtils";
import AddMediaToParentDropDown from "../../common/AddMediaToParentDropdown";
import BulkStatusChangeButton from "./BulkStatusChangeButton";
import BulkStatusConfirmDialog from "./BulkStatusConfirmDialog";
import RejectedDialog from "../../organize/review/RejectedDialog";
import MediaBrowser from "../../common/MediaBrowser";
import ContentFacetMenu from "./ContentFacetMenu";
import FacetPills from "../../common/FacetPills";
import QueryPills from "../../common/QueryPills";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";

import { useSearchQuery } from "../../common/use-searchQuery";
import { useSnackAlert } from "../../../common/use-snackAlert";
import SnackAlert from "../../../components/SnackAlert";
import { initialStatus } from "../ContentView";
import { Checkbox, FormControlLabel, Typography } from "@mui/material";
import { useContentUpdate } from "../contentServices";
import { useAddToModule } from "../../modules/moduleServices";
import { useAddToCollection } from "../../collections/collectionServices";
import LoadingIndicator from "../../../components/LoadingIndicator";

const useStyles = makeStyles(theme => ({
  icon: {
    "& > :first-child": {
      fontSize: "1.4rem",
    },
  },

  select: {
    fontWeight: 700,
    color: "black",
  },

  showDeleted: {
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
}));

// remove (-status:* AND *:*) as option for facet menu
const getStatusTerms = appliedFacets => {
  const status = find(propEq("name", "status"), appliedFacets);
  const terms = status?.terms;
  return terms ?? [];
};

const ContentBrowser = props => {
  const {
    searchState,
    facetState,
    onSearchStateChange,
    onFacetStateChange,
    searchResults,
    facetResults,
    onSaveSuccess,
    setSearchPreferences,
  } = props;

  const classes = useStyles();

  const [contentList, setContentList] = useState([]);
  useEffect(() => {
    if (searchResults.isSuccess) {
      setContentList(searchResults.data?.media ?? []);
    }
  }, [searchResults.isSuccess, searchResults.data]);

  const [scrollVisible, setScrollVisible] = useState(true);
  const [contentSelected, setContentSelected] = useState([]);
  const [selectedFacets, setSelectedFacets] = useState({});

  const reduxDispatch = useDispatch();

  //-- Manage the alert message
  const snackAlertHandler = useSnackAlert();
  const { showAlert } = snackAlertHandler;

  //-- Bulk status update
  const updateContent = useContentUpdate();
  const addToModule = useAddToModule();
  const addToCollection = useAddToCollection();
  const [bulkStatusDialog, setBulkStatusDialog] = useState({
    open: false,
    targetStatus: null,
  });
  const [undoSnapshot, setUndoSnapshot] = useState(null);

  //-- Handle Search state
  const initialSearchState = clone(searchState);
  const searchQuery = useSearchQuery(initialSearchState);

  //-- Whenever the internal search searchQuery.state changes, update the search searchQuery.state stored in the parent
  useEffect(() => {
    onSearchStateChange(searchQuery.state);
    setScrollVisible(true);
  }, [onSearchStateChange, setScrollVisible, searchQuery.state]);

  useEffect(() => {
    const selectedFacets = omit(
      ["type", "contentType", "omniType", "status", "q"],
      normalizeAppliedFacets(searchQuery.state.appliedFacets),
    );
    setSelectedFacets(selectedFacets);
  }, [searchQuery.state.appliedFacets]);

  //-- Handle Facet state
  const initialFacetState = clone(facetState);
  const facetQuery = useSearchQuery(initialFacetState);
  useEffect(() => {
    onFacetStateChange(facetQuery.state);
  }, [onFacetStateChange, facetQuery.state]);

  //-- Manage the content type facets
  const [contentTypes, setContentTypes] = useState(null);
  const [contentTypeTerms, setContentTypeTerms] = useState([]);
  useEffect(() => {
    if (facetResults.isError) {
      //-- Send user to login if the token has expired or not logged in
      if (facetResults.error.status === 401) {
        reduxDispatch(logOut());
      }
    }

    if (facetResults.isSuccess) {
      //-- Get the content type facets
      const facet = facetResults.data?.facets?.contentType;
      const contentTypesFacet = head(normalizeFacetResults([facet]));
      if (notBlank(contentTypesFacet)) {
        setContentTypes(contentTypesFacet);

        //-- Get any previously applied content type facets
        const appliedFacet = find(
          propEq("name", contentTypesFacet.name),
          searchQuery.state.appliedFacets,
        );
        setContentTypeTerms(appliedFacet ? appliedFacet.terms : []);
      } else {
        //-- No matching content, so there are no content types to filter by
        setContentTypes(null);
        setContentTypeTerms([]);
      }
    }
  }, [
    facetResults.isError,
    facetResults.isSuccess,
    facetResults.data,
    facetResults.error,
    searchQuery.state,
    reduxDispatch,
  ]);

  //-- Manage other filters, i.e. not content type
  const [otherAvailableFilters, setOtherAvailableFilters] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({});
  useEffect(() => {
    if (facetResults.isSuccess) {
      const facets = facetResults.data?.facets;

      //-- remove all type facets
      const normalizedFacets = reject(
        ({ name }) => any(equals(name), ["type", "contentType", "omniType"]),
        normalizeFacetResults(values(facets ?? {})),
      );

      setOtherAvailableFilters(normalizedFacets);
    }
  }, [facetResults.isSuccess, facetResults.data]);

  //-- Event handlers
  const [totalSelectedTerms, setTotalSelectedTerms] = useState(0);

  const handleFacetsSelected = useCallback(
    newSelectedFacet => {
      const prevSelectedFilters = omit(
        ["type", "contentType", "omniType"],
        normalizeAppliedFacets(searchQuery.state.appliedFacets),
      );

      //-- remove facet with empty terms list
      let selectedFilters;
      if (isEmpty(newSelectedFacet.terms)) {
        const { name } = newSelectedFacet;
        const prevFacets = prevSelectedFilters;
        delete prevFacets[name];
        selectedFilters = prevFacets;
      } else {
        selectedFilters = {
          ...prevSelectedFilters,
          [newSelectedFacet.name]: newSelectedFacet,
        };
      }

      //-- Update filter button count
      const termCount = getTotalSelectedTerms(selectedFilters);
      setTotalSelectedTerms(termCount);

      //-- Keep only facets for type and content type
      const initialFacetState = filter(
        either(propEq("name", "type"), propEq("name", "contentType")),
        searchQuery.state.appliedFacets,
      );

      //-- Add newly selected facets
      const facets = concat(values(selectedFilters), initialFacetState);
      searchQuery.handleUpdateFacets(facets);
      facetQuery.handleUpdateFacets(facets);
    },
    [searchQuery, facetQuery],
  );

  const handleClearFilters = useCallback(() => {
    setTotalSelectedTerms(0);

    //-- Keep only facet for type="CONTENT" and reset to initial status filter
    const initialFacetState = pipe(
      filter(propEq("name", "type")),
      append(initialStatus),
    )(searchQuery.state.appliedFacets);

    searchQuery.handleUpdateFacets(initialFacetState);
    facetQuery.handleUpdateFacets(initialFacetState);
  }, [searchQuery, facetQuery]);

  const handleClearFacetPill = useCallback(
    facet => {
      const updatedFacets = reject(
        propEq("name", facet.name),
        searchQuery.state.appliedFacets,
      );

      // update selected filter list for filter drop down menu
      const updatedSelectedFilters = omit([facet.name], selectedFilters);
      setSelectedFilters(updatedSelectedFilters);

      // update selected filter count for filter drop down menu
      setTotalSelectedTerms(getTotalSelectedTerms(updatedSelectedFilters));

      // update searchQuery.state.appliedFacets
      searchQuery.handleUpdateFacets(updatedFacets);
      facetQuery.handleUpdateFacets(updatedFacets);
    },
    [searchQuery, facetQuery, selectedFilters, setSelectedFilters],
  );

  const handleStatusClick = status => event => {
    const checked = event.target.checked;

    // Toggle status
    const updatedFacets = map(facet => {
      if (facet.name === "status") {
        let terms = !checked
          ? reject(propEq("code", status), facet.terms)
          : append({ code: status }, facet.terms);
        return { ...facet, terms: uniqBy(t => t.code, terms) };
      }

      return facet;
    }, searchQuery.state.appliedFacets);

    // update searchQuery.state.appliedFacets
    searchQuery.handleUpdateFacets(updatedFacets);
    facetQuery.handleUpdateFacets(updatedFacets);
  };

  //-- Manage nested menu items
  const [selectedFacet, setSelectedFacet] = useState("");
  const handleFacetClick = facetName => {
    equals(selectedFacet, facetName)
      ? setSelectedFacet("")
      : setSelectedFacet(facetName);
  };

  const handleFacetPillClick = facetName => {
    setSelectedFacet(facetName);
  };

  const handleContentTypeSelected = useCallback(
    term => {
      //-- Nothing to filter on when the search returned no content types
      if (isBlank(contentTypes)) {
        return;
      }

      //-- If the term already exists in the list then remove it; if not then add it
      const terms = any(propEq("label", term.label), contentTypeTerms)
        ? reject(propEq("label", term.label), contentTypeTerms)
        : append(term, contentTypeTerms);

      //-- Build the new facet with the updated terms
      const facet = {
        name: contentTypes.name,
        queryParam: contentTypes.queryParam,
        terms: terms,
      };

      //-- Remove the old facet and add in the new one
      const facets = append(
        facet,
        reject(
          propEq("name", contentTypes.name),
          searchQuery.state.appliedFacets,
        ),
      );
      searchQuery.handleUpdateFacets(facets);
      facetQuery.handleUpdateFacets(facets);
    },
    [contentTypeTerms, contentTypes, searchQuery, facetQuery],
  );

  const handleClearContentType = useCallback(() => {
    //-- Clear out the content type facet
    setContentTypeTerms([]);
    if (isBlank(contentTypes)) {
      return;
    }

    const facets = reject(
      propEq("name", contentTypes.name),
      searchQuery.state.appliedFacets,
    );
    searchQuery.handleUpdateFacets(facets);
    facetQuery.handleUpdateFacets(facets);
  }, [searchQuery, facetQuery, contentTypes]);

  const handleScrollVisible = useCallback(visible => {
    setScrollVisible(visible);
  }, []);

  const [disableAddToSubcollection, setDisableAddToSubcollection] = useState(
    true,
  );

  const handleSelectChange = useCallback(selected => {
    setDisableAddToSubcollection(isEmpty(selected));
    setContentSelected(selected);
  }, []);

  const handleFilterDelete = useCallback(
    value => {
      searchQuery.handleUpdateQuery(
        reject(equals(value), searchQuery.state.appliedQuery),
      );
      facetQuery.handleUpdateQuery(
        reject(equals(value), searchQuery.state.appliedQuery),
      );
    },
    [searchQuery, facetQuery],
  );

  const handleUpdateQuery = useCallback(
    query => {
      searchQuery.handleUpdateQuery(query);
      facetQuery.handleUpdateQuery(query);
    },
    [searchQuery, facetQuery],
  );

  const handleUpdateTitleOnly = useCallback(
    event => {
      const checked = event.target.checked;
      searchQuery.handleUpdateTitleOnly(checked);
      facetQuery.handleUpdateTitleOnly(checked);

      setSearchPreferences(prev => ({ ...prev, titleOnly: checked }));
    },
    [searchQuery, facetQuery, setSearchPreferences],
  );

  //-- Replacing Search Term in Query Pill
  const [editSearchModal, setEditSearchModal] = useState(false);
  const [previousSearchTerm, setPreviousSearchTerm] = useState("");

  const handleEditSearchModalClose = () => setEditSearchModal(false);

  const handleQueryPillClick = queryPart => {
    setEditSearchModal(true);
    setPreviousSearchTerm(queryPart);
  };

  const handleEditSearchApply = useCallback(
    newSearchTerm => {
      const appliedQuery = searchQuery.state.appliedQuery;

      const withoutPreviousTerm = reject(
        equals(previousSearchTerm),
        appliedQuery,
      );

      const updatedQuery = isBlank(newSearchTerm)
        ? withoutPreviousTerm
        : append(newSearchTerm, withoutPreviousTerm);

      searchQuery.handleUpdateQuery(updatedQuery);
      facetQuery.handleUpdateQuery(updatedQuery);

      handleEditSearchModalClose();
      setPreviousSearchTerm("");
    },
    [previousSearchTerm, searchQuery, facetQuery],
  );

  // Manage Selected Content
  const {
    state,
    handleSelectAllContent,
    handleRowClick,
    handleRemoveAllSelected,
  } = useMediaTable(
    { onSelectChange: handleSelectChange },
    { selected: contentSelected },
  );

  const selectedContentItems = contentList.filter(item =>
    contentSelected.includes(item.id),
  );

  const executeStatusChange = useCallback(
    async status => {
      const snapshot = selectedContentItems.map(item => ({
        id: item.id,
        previousStatus: item.status,
        parents: [
          ...(item.parentModules ?? []),
          ...(item.parentCollections ?? []),
        ],
      }));
      setUndoSnapshot(snapshot);
      try {
        await Promise.all(
          selectedContentItems.map(item =>
            updateContent.mutateAsync({
              resourceIds: { id: item.id },
              data: { status },
            }),
          ),
        );
        showAlert("success", [`Status updated to ${status}`], true);
        handleRemoveAllSelected();
      } catch (err) {
        showAlert("error", [err.message]);
      }
    },
    [selectedContentItems, updateContent, showAlert, handleRemoveAllSelected],
  );

  const handleStatusSelect = useCallback(
    status => {
      if (status === "REJECTED") {
        // Capture snapshot before dialog opens — RejectedDialog runs its own mutations
        const snapshot = selectedContentItems.map(item => ({
          id: item.id,
          previousStatus: item.status,
          parents: [
            ...(item.parentModules ?? []),
            ...(item.parentCollections ?? []),
          ],
        }));
        setUndoSnapshot(snapshot);
        setBulkStatusDialog({ open: true, targetStatus: "REJECTED" });
      } else if (status === "PENDING" || status === "DELETED") {
        setBulkStatusDialog({ open: true, targetStatus: status });
      } else {
        executeStatusChange(status);
      }
    },
    [executeStatusChange, selectedContentItems],
  );

  const handleBulkReject = useCallback(() => {
    setBulkStatusDialog({ open: false, targetStatus: null });
    handleRemoveAllSelected();
    showAlert("success", ["Items rejected"], true);
  }, [handleRemoveAllSelected, showAlert]);

  const handleUndo = useCallback(async () => {
    if (!undoSnapshot) return;
    try {
      await Promise.all(
        undoSnapshot.map(async ({ id, previousStatus, parents }) => {
          await updateContent.mutateAsync({
            resourceIds: { id },
            data: { status: previousStatus },
          });
          await Promise.all(
            parents.map(parent => {
              if (parent.mediaType === "module") {
                return addToModule.mutateAsync({
                  resourceIds: { id: parent.id },
                  data: { ids: [id] },
                });
              } else if (parent.mediaType === "collection") {
                return addToCollection.mutateAsync({
                  resourceIds: { id: parent.id },
                  data: { ids: [id] },
                });
              }
              return Promise.resolve();
            }),
          );
        }),
      );
      setUndoSnapshot(null);
      showAlert("success", ["Status change undone"]);
    } catch (err) {
      showAlert("error", [err.message]);
    }
  }, [undoSnapshot, updateContent, addToModule, addToCollection, showAlert]);

  // Status
  const statusTerms = getStatusTerms(searchQuery.state.appliedFacets);

  //-- Nothing matched the current search/filters
  const noResults = searchResults.isSuccess && isEmpty(contentList);
  const appliedQuery = searchQuery.state.appliedQuery;
  const noResultsMessage = notBlank(appliedQuery)
    ? `No results for "${join(", ", appliedQuery)}"`
    : "No content matches these filters";

  return (
    <Fragment>
      {anyTrue([
        updateContent.isPending,
        addToModule.isPending,
        addToCollection.isPending,
      ]) && <LoadingIndicator transparent />}
      <SnackAlert
        handler={snackAlertHandler}
        duration={15000}
        action={
          <Button
            variant="outlined"
            style={{ color: "white" }}
            onClick={handleUndo}
          >
            Undo
          </Button>
        }
      />
      <BulkStatusConfirmDialog
        open={
          bulkStatusDialog.open &&
          includes(bulkStatusDialog.targetStatus, ["DELETED", "PENDING"])
        }
        targetStatus={bulkStatusDialog.targetStatus}
        contentItems={selectedContentItems}
        onConfirm={() => {
          const status = bulkStatusDialog.targetStatus;
          setBulkStatusDialog({ open: false, targetStatus: null });
          executeStatusChange(status);
        }}
        onClose={() => setBulkStatusDialog({ open: false, targetStatus: null })}
      />
      <RejectedDialog
        open={
          bulkStatusDialog.open && bulkStatusDialog.targetStatus === "REJECTED"
        }
        content={selectedContentItems}
        handleClose={() =>
          setBulkStatusDialog({ open: false, targetStatus: null })
        }
        handleReject={handleBulkReject}
      />
      <EditSearchModal
        editSearchModal={editSearchModal}
        previousSearchTerm={previousSearchTerm}
        handleEditSearchModalClose={handleEditSearchModalClose}
        handleEditSearchApply={handleEditSearchApply}
      />
      <MediaBrowser
        loaded={searchResults.isSuccess}
        scrollVisible={scrollVisible}
        searchBar={
          <Grid container style={{ padding: "0 15px" }}>
            <Grid container item alignItems="center">
              <ContentTotalsHeader
                count={
                  searchResults.isSuccess ? searchResults.data.totalCount : "-"
                }
              />
              <ContentTypeFilters
                loading={facetResults.isLoading}
                contentTypes={contentTypes}
                contentTypeTerms={contentTypeTerms}
                onContentTypeSelected={handleContentTypeSelected}
                onClear={handleClearContentType}
              />
              <Grid item>
                <ContentSearchBar
                  query={searchQuery.state.appliedQuery}
                  onQueryChange={handleUpdateQuery}
                  filterMenu={
                    <Grid container justifyContent="center" alignItems="center">
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="titleOnly"
                            color="primary"
                            checked={searchQuery.state.titleOnly}
                            onChange={handleUpdateTitleOnly}
                          />
                        }
                        label="Title Only"
                      />
                      <ContentFacetMenu
                        facets={map(
                          facet => {
                            const isSelected = Boolean(
                              selectedFacets[facet.name],
                            );
                            const selectedTerms =
                              facet.name === "status"
                                ? statusTerms
                                : isSelected
                                ? selectedFacets[facet.name].terms
                                : [];
                            return mergeRight(facet, {
                              selected: isSelected,
                              selectedTerms: selectedTerms,
                            });
                          },
                          append(
                            // manually add status
                            {
                              name: "status",
                              displayName: "Status",
                            },
                            otherAvailableFilters,
                          ),
                        )}
                        handleClearFilters={handleClearFilters}
                        handleFacetClick={handleFacetClick}
                        handleFacetSelection={handleFacetsSelected}
                        handleStatusClick={handleStatusClick}
                        loading={facetResults.isLoading}
                        selectedFacet={selectedFacet}
                        selectedFacets={selectedFacets}
                        totalSelectedTerms={totalSelectedTerms}
                      />
                    </Grid>
                  }
                />
              </Grid>
            </Grid>
            <Grid container item alignItems="center">
              <Grid item>
                <QueryPills
                  handleClick={handleQueryPillClick}
                  appliedQuery={searchQuery.state.appliedQuery}
                  handleDelete={handleFilterDelete}
                />
                <FacetPills
                  facetPills={selectedFacets}
                  handleClearFacet={handleClearFacetPill}
                  handleClick={handleFacetPillClick}
                />
              </Grid>
              <Grid
                item
                container
                alignItems="center"
                justifyContent="flex-end"
              >
                {!isEmpty(contentSelected) && (
                  <Grid item style={{ marginRight: "auto" }}>
                    <Tooltip title="Remove All Selected" placement="bottom">
                      <Button
                        onClick={() => handleRemoveAllSelected()}
                        startIcon={<FontAwesomeIcon icon={faSquareX} />}
                        classes={{
                          root: classes.select,
                          iconSizeMedium: classes.icon,
                        }}
                      >
                        {" " + length(contentSelected) + " selected"}
                      </Button>
                    </Tooltip>
                  </Grid>
                )}

                <Grid item>
                  <AddMediaToParentDropDown
                    parent="collection"
                    disableButton={disableAddToSubcollection}
                    childList={contentSelected}
                    showAlert={showAlert}
                    handleAddSuccess={onSaveSuccess}
                  />
                </Grid>
                <Grid item>
                  <AddMediaToParentDropDown
                    parent="module"
                    disableButton={disableAddToSubcollection}
                    childList={contentSelected}
                    showAlert={showAlert}
                    handleAddSuccess={onSaveSuccess}
                  />
                </Grid>
                <Grid item>
                  <BulkStatusChangeButton
                    disabled={disableAddToSubcollection}
                    onStatusSelect={handleStatusSelect}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        }
        mediaTable={
          noResults ? (
            <Grid
              container
              direction="column"
              justifyContent="center"
              alignItems="center"
              sx={{ height: "calc(100vh - 300px)" }}
            >
              <Grid item>
                <Typography>{noResultsMessage}</Typography>
              </Grid>
            </Grid>
          ) : (
            <ContentTable
              contentList={contentList}
              total={
                searchResults.isSuccess ? searchResults.data.totalCount : 0
              }
              page={searchQuery.state.page}
              orderBy={searchQuery.state.orderBy}
              order={searchQuery.state.orderDirection}
              rowsPerPage={searchQuery.state.limit}
              onSortChange={searchQuery.handleUpdateSort}
              onPageChange={searchQuery.handleUpdatePage}
              onRowsPerPageChange={searchQuery.handleUpdateLimit}
              onSelectChange={handleSelectChange}
              onSelectAllContent={handleSelectAllContent}
              onRowClick={handleRowClick}
              searchState={searchQuery.state}
              selected={state.selected}
              onScrollVisible={handleScrollVisible}
            />
          )
        }
      />
    </Fragment>
  );
};

export default ContentBrowser;
